<template>
	<div class="filter-container">
		<h3
			v-if="label"
			class="heading"
			data-test="heading"
		>
			Team
		</h3>
		<BaseButton
			ariaLabel="filter by owner"
			class="owner-button"
			color="white"
			:class="{ 'no-label': !label }"
			@onClick="toggleOwnerMenu"
		>
			<div class="button-content">
				<span>Owner</span>
				<div
					v-if="caret"
					class="caret-container"
				>
					<font-awesome-icon
						class="caret"
						data-test="caret"
						:class="{ open: showOwnerMenu }"
						:icon="['fas', 'caret-right']"
					/>
				</div>
			</div>
		</BaseButton>
		<span
			v-if="counter > 0"
			class="counter"
			data-test="counter"
			:class="{ 'no-label': !label }"
		>
			{{ counter }}
		</span>

		<transition name="fade">
			<div
				v-if="showOwnerMenu"
				class="menu-container"
				data-test="menu-container"
				:class="{ 'no-label': !label }"
			>
				<div class="menu-header">
					<h3 class="heading">Brokers</h3>
					<TextButton
						aria-label="Reset filters"
						@onClick="handleResetButtonClick"
					>
						Reset
					</TextButton>
				</div>
				<div class="contact-filter">
					<SearchInput
						ariaLabel="'Search for Owners'"
						placeholder="Search..."
						@onSearchInputChange="handleSearchInputChange"
					/>
					<div class="contact-list">
						<div
							v-for="contact in availableBrokers"
							class="contact"
							data-test="contact"
							role="button"
							:aria-label="`for ${contact.firstName} ${contact.lastName}`"
							:key="contact.rolodexContactId"
							@click="handleContactClick(contact)"
						>
							<CheckBox
								ref="checkBox"
								:checked="isChecked(contact)"
								@onClick="handleContactClick(contact)"
							/>
							<div class="full-name">
								{{
									getTruncatedName(
										contact.firstName,
										contact.lastName,
									)
								}}
							</div>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { isEmpty, truncate, debounce } from 'lodash';
import BaseButton from '@/components/global/BaseButton.vue';
import CheckBox from './CheckBox.vue';
import SearchInput from '@/components/global/SearchInput.vue';
import TextButton from '@/components/global/TextButton.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
	name: 'OwnerFilter',
	props: {
		brokerList: Array,
		selectedBrokers: Array,
		label: {
			type: Boolean,
			default: false,
		},
		caret: {
			type: Boolean,
			default: false,
		},
		onInit: Boolean,
		cancelBrokerChange: Boolean,
	},
	components: {
		CheckBox,
		FontAwesomeIcon,
		SearchInput,
		TextButton,
		BaseButton,
	},
	data() {
		return {
			showOwnerMenu: false,
			counter: 0,
			availableBrokers: [],
			localSelectedBrokers: [],
		};
	},
	methods: {
		isChecked({ contactId, rolodexContactId }) {
			const id = rolodexContactId || contactId;
			return this.localSelectedBrokers.includes(id);
		},

		isMatchingContact(contact, searchTerm) {
			return `${contact.firstName}${contact.lastName}`
				.toLowerCase()
				.includes(searchTerm.replace(/\s+/g, '').toLowerCase());
		},
		handleSearchInputChange(searchTerm) {
			if (isEmpty(searchTerm)) {
				this.availableBrokers = [...this.brokerList];
			} else {
				this.availableBrokers = this.brokerList.filter((contact) =>
					this.isMatchingContact(contact, searchTerm),
				);
			}
		},
		getTruncatedName(firstName, lastName) {
			return firstName || lastName
				? truncate(`${firstName || ''} ${lastName || ''}`, {
						length: 32,
				  })
				: '';
		},
		handleContactClick({ contactId, rolodexContactId }) {
			const eventId = rolodexContactId || contactId;
			if (this.localSelectedBrokers.includes(eventId)) {
				this.localSelectedBrokers =
					this.localSelectedBrokers.filter((id) => id !== eventId);
			} else {
				this.localSelectedBrokers.push(eventId);
			}
			this.setCounter();
			this.emitFilterChange();
		},
		toggleOwnerMenu() {
			this.showOwnerMenu = !this.showOwnerMenu;
		},
		handleResetButtonClick() {
			this.localSelectedBrokers = [];
			this.setCounter();
			this.emitFilterChange();
			/**
			 * when a user selects a checkbox then clicks reset before 1 second has passed
			 * this prevents the checkbox from showing as checked when in the state it is
			 * not checked
			 */
			this.$refs.checkBox.forEach((e) => e.reset());
		},
		setCounter() {
			this.counter = this.localSelectedBrokers.length;
		},
		clickHandler(e) {
			if (!this.$el.contains(e.target) && this.showOwnerMenu) {
				this.showOwnerMenu = false;
				this.availableBrokers = [...this.brokerList];
			}
		},
		emitFilterChange: debounce(function () {
			this.$emit('onBrokerFilterChange', [...this.localSelectedBrokers]);
		}, 1000),
	},
	watch: {
		brokerList(newBrokers) {
			this.availableBrokers = [...newBrokers];
		},
		onInit(value) {
			if (value) {
				this.localSelectedBrokers = [];
				this.counter = 0;
			}
		},
		cancelBrokerChange(value) {
			if (value) {
				this.localSelectedBrokers = [...this.selectedBrokers];
				this.setCounter();
				this.$emit('onCancelBrokerChange');
			}
		}
	},
	mounted() {
		window.addEventListener('click', this.clickHandler);
		this.availableBrokers = [...this.brokerList];
		this.localSelectedBrokers = [...this.selectedBrokers];
		this.setCounter();
	},
	beforeDestroy() {
		window.removeEventListener('click', this.clickHandler);
	},
};
</script>

<style scoped lang="scss">
.base-button.owner-button {
	transition: all 0.3s ease;
	@include rem('margin-top', 10px);
	@include rem('font-size', 15px);
	@include rem('padding', 11px 8px);
	max-width: 100%;
	height: auto;
	white-space: nowrap;

	&:hover,
	&:focus,
	&:active,
	&.active {
		background-color: $highlight-color;
		border: 1px solid $brand-blue;
		cursor: pointer;
		outline: none;
	}
	.button-content {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.caret {
		margin-left: 8px;
		transition: rotate 0.25s;
	}

	.open {
		rotate: 90deg;
		transition: rotate 0.25s;
	}
}

.base-button.no-label {
	margin: 0;
}

.contact-filter {
	@include rem('margin-top', 8px);

	.contact-list {
		@include rem('max-height', 350px);
		@include rem('padding', 5px 0);
		overflow-y: scroll;
	}

	.contact {
		display: grid;
		grid-template-columns: 12% auto;
		align-items: center;
		@include rem('margin', 0 8px);
		@include rem('height', 34px);
		cursor: pointer;

		&:hover {
			background: $border-color-light;
			border-radius: 6px;
		}
	}

	/deep/ .custom-checkbox {
		@include rem('margin-left', 8px);
	}

	/deep/ .search-input {
		width: 94%;
		@include rem('height', 36px);
		@include rem('margin', 0 8px);
	}

	/deep/ .search-icon {
		@include rem('top', 9px);
	}
}

.filter-container {
	position: relative;
}

.menu-container {
	box-sizing: border-box;
	background-color: $white;
	border-radius: $b360-border-radius-large;
	border: 1px solid $brand-blue;
	box-shadow: 0 3px 10px $shadow-color;
	z-index: 2;
	position: absolute;
	top: calc(100% + 10px);
	left: 0;
	opacity: 1;
	@include rem('width', 320px);
	z-index: 3;
}

.menu-container.no-label {
	right: 0;
	left: unset;
	border: 1px solid $border-color-light;
}

.heading {
	margin: 0;
	@include rem('font-size', 16px);
}

.menu-header {
	display: flex;
	@include rem('padding', 16px);
	justify-content: space-between;
	border-bottom: 1px solid $border-color-light;
}

.counter {
	background: $brand-blue;
	color: $white;
	font-weight: bold;
	line-height: 1;
	position: absolute;
	@include rem('top', 17px);
	@include rem('right', -13px);
	@include rem('border-radius', 6px);
	display: flex;
	justify-content: center;
	align-items: center;
	@include rem('font-size', 14px);
	@include rem('width', 26px);
	@include rem('height', 26px);
}

.counter.no-label {
	@include rem('top', -13px);
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-active {
	opacity: 0;
}

@media screen and (max-width: 750px) {
	.base-button.owner-button {
		white-space: normal;
	}
}
@media screen and (max-width: 400px) {
	.menu-container {
		right: -46px;
	}
	.menu-container.no-label {
		right: -46px;
	}
}
</style>
